<template>
  <div class="greyactivitybackground" :class="mobile() ? 'activity-page-container-mobile' : 'activity-page-container'">
    <AlertMessages v-if="alertValue" :alert-type="alertType" :alert-msg="alertMsg" @reset="alertValue = false" />

    <!-- EDIT MODE -->
    <v-card max-width="960px" width="100%" height="100%"
      class="activity-page-height edit-mode edit-mode-checklist mx-auto">
      <v-row v-if="coverURL == null && !loadingData" no-gutters
        class="v-bottom-navigation elevation-0 v-item-group bigButtonBackground align-center justify-center"
        style="height:330px; width: 100%">
        <v-col cols="12" class="pa-12 list-group-item" style="height: 100%" @click="showUploadChecklistImage()">
          <div class="custom-border" style="height: 100%">
            <v-btn text data-testid="upload-button-center" :disabled="loadingImage" color="white"
              class="no-hover text-body-1 darktext--text text-none rounded-xxl px-4 mx-auto d-block" :ripple="false">
              <span class="grey--text">{{ loadingImage ? $t('uploading') : $t('uploadCoverImage') }}</span>
              <v-icon v-if="!loadingImage" size="124" color="grey" class="pa-2">
                mdi-image-plus
              </v-icon>
              <v-progress-circular v-if="loadingImage" size="72" indeterminate class="text-center justify-center ma-8" />
            </v-btn>
          </div>
        </v-col>
      </v-row>
      <v-row v-if="coverURL != null && !loadingData" no-gutters>
        <v-col cols="12 list-group-item">
          <v-toolbar flat class="d-none" height="32">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon class="d-block pa-1" data-testid="delete-cover-icon" dense color="accent" v-on="on"
                  @click="removeImage()">
                  mdi-delete
                </v-icon>
              </template>
              <span>{{ $t('removeCoverImage') }}</span>
            </v-tooltip>
            <v-spacer />
          </v-toolbar>
          <v-img :src="coverURL" width="100%" max-height="330px" data-testid="cover-image"
            @click="showUploadChecklistImage" />
        </v-col>
      </v-row>
      <v-file-input ref="file_input" v-model="imageData" accept="image/png, image/jpeg" data-testid="file-input"
        class="d-none" @change="uploadImage" />
      <v-row v-if="!loadingData" no-gutters>
        <v-col cols="12" class="list-group-item">
          <v-toolbar flat class="d-none" height="32">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon class="d-block pa-1" data-testid="delete-description-icon" dense color="accent" v-on="on"
                  @click="removeDescription()">
                  mdi-delete
                </v-icon>
              </template>
              <span>{{ $t('removeDescription') }}</span>
            </v-tooltip>
            <v-spacer />
          </v-toolbar>
          <div class="content-block">
            <div class="px-8 py-4">
              <editor-checklist ref="editDescription" :key="updateEditorDescription" data-testid="description-editor"
                :content="description" part="description" @proceedClick="setEditor" @updateContent="updateContent"
                @proceedMark="showHideEditorTextOptions" @enableEditMode="showEditMode = true, showBlocks = false" />
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- CHECKLIST -->

      <draggable v-if="!checklist.length && !loadingData" ref="dragArea" class="dragAreEmptyChecklist list-group mx-auto"
        :list="checklist" group="blocks" ghost-class="ghost-block" data-testid="checklist-empty-list" @change="log">
        <v-row no-gutters data-testid="empty-page-content" align="center" class="fill-height">
          <v-col cols="12" class="mx-auto pa-0" align="center">
            <div class="greytextlight--text text-h6 text-md-h5 font-weight-bold mx-auto pa-4">
              {{ $t('emptyEditActivityChecklistText') }}
            </div>
            <div ref="editChecklist" />
          </v-col>
        </v-row>
      </draggable>
      <!-- :draggable property is added because was having problems in some browsers -->
      <!-- https://github.com/ueberdosis/tiptap/issues/379 -->
      <!--         v-if="checklist.length>0 && !loadingData"
 -->
      <v-form ref="form" v-model="valid" v-else>
        <draggable ref="dragArea" class="list-group py-4" :list="checklist" group="blocks" ghost-class="ghost-block"
          :sort="false" :draggable="showBlocks" data-testid="checklist-list" @change="log">
          <div v-for="(element, index) in checklist" :key="index" class="list-group-item"
            :class="{ 'first': index == 0, 'last': index == checklist.length - 1 }">
            <v-icon v-if="index != 0" class="moveup-icon d-none" color="accent" data-testid="moveup-icon" dense
              @click="moveBlock(index, index - 1)">
              mdi-chevron-up
            </v-icon>
            <v-toolbar flat class="d-none">
              <v-tooltip :bottom="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm || $vuetify.breakpoint.md"
                :right="$vuetify.breakpoint.lg || $vuetify.breakpoint.xl">
                <template v-slot:activator="{ on }">
                  <v-icon class="d-block pa-1" data-testid="delete-icon" dense color="accent" v-on="on"
                    @click="deleteBlock(index)">
                    mdi-delete
                  </v-icon>
                </template>
                <span>{{ $t('deleteBlock') }}</span>
              </v-tooltip>
              <v-tooltip :bottom="$vuetify.breakpoint.xs || $vuetify.breakpoint.sm || $vuetify.breakpoint.md"
                :right="$vuetify.breakpoint.lg || $vuetify.breakpoint.xl">
                <template v-slot:activator="{ on }">
                  <v-icon class="d-block pa-1" data-testid="copy-icon" dense color="accent" v-on="on"
                    @click="copyBlock($event, element, index)">
                    mdi-content-copy
                  </v-icon>
                </template>
                <span>{{ $t('duplicateBlock') }}</span>
              </v-tooltip>
              <v-spacer />
            </v-toolbar>

            <!-- eslint-disable vue/no-v-html -->
            <v-divider class="mx-md-15" />

            <div class="content-block px-8">
              <div class="px-md-8 py-5">
                <v-row align="center" no-gutters>
                  <v-checkbox :off-icon="'mdi-square-rounded-outline'" value="false" hide-details
                    class="shrink mr-2 mt-0 checklist-off-icon" color="accent" disabled />
                  <v-text-field v-model="element.title" solo hide-details="auto" elevation="0" :rules="titleRules"
                    @click="disableDesigning" />
                </v-row>
                <editor-checklist ref="editChecklist" :key="updateEditorChecklist" :content="element.content"
                  :index="index" part="checklist" @proceedClick="setEditor" @updateContent="updateChecklist"
                  @proceedMark="showHideEditorTextOptions" @enableEditMode="showEditMode = true, showBlocks = false" />
              </div>
            </div>
            <v-icon v-if="index != (checklist.length - 1)" class="movedown-icon d-none" color="accent"
              data-testid="movedown-icon" dense @click="moveBlock(index, index + 1)">
              mdi-chevron-down
            </v-icon>
          </div>
        </draggable>
      </v-form>

    </v-card>

    <v-navigation-drawer app permanent right clipped data-testid="navigation-drawer" width="300">
      <template v-if="!loadingData" v-slot:prepend>
        <v-row class="ma-0">
          <v-col cols="5">
            <v-tooltip v-if="loadingAutosave" top>
              <template v-slot:activator="{ on }">
                <v-icon data-testid="saving-draft-icon" dense class="mr-2" v-on="on"
                  @click="autosaveChecklistAsDraft(false)">
                  mdi-cloud-sync-outline
                </v-icon>
              </template>
              <span>{{ $t('savingTooltip') }}</span>
            </v-tooltip>
            <v-tooltip v-else top>
              <template v-slot:activator="{ on }">
                <v-icon data-testid="save-draft-icon" dense class="mr-2" v-on="on"
                  @click="autosaveChecklistAsDraft(false)">
                  mdi-cloud-outline
                </v-icon>
              </template>
              <span>{{ $t('saveAsDraftTooltip') }}</span>
            </v-tooltip>
            <v-tooltip v-if="draftVersion" top>
              <template v-slot:activator="{ on }">
                <v-icon data-testid="discard-draft-icon" dense v-on="on" @click="
                  dialogDraftVisible = true,
                  confirmationDialogTitle = $t('confirmationDialogTitleDiscardDraft'),
                  confirmationDialogText = $t('confirmationDialogTextDiscardDraft'),
                  confirmationDialogSubmitBtn = $t('discard')
                confirmationDialogCancelBtn = $t('no')">
                  mdi-backup-restore
                </v-icon>
              </template>
              <span>{{ $t('discardAllDraftChanges') }}</span>
            </v-tooltip>
            <div class="text-caption grey--text font-weight-regular pt-1" style="height:20px; line-height:1;">
              {{ loadingAutosaveFinish ? $t('saveAsDraftSuccess') : '' }}
            </div>
          </v-col>
          <v-col cols="7" align="right">
            <v-btn data-testid="submit-button" class="rounded-xxl text-none px-10" color="success" dark @click="
              dialogPublishVisible = true,
              confirmationDialogTitle = $t('confirmationDialogTitlePublishChecklist'),
              confirmationDialogText = $t('confirmationDialogTextPublishChecklist'),
              confirmationDialogSubmitBtn = $t('publish')
            confirmationDialogCancelBtn = $t('saveAsDraft')
              ">
              {{ $t('publish') }}
            </v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6">
            <div class="d-flex flex-column" style="align-items:center">
              <v-btn icon class="mr-0 text-none" :class="{ 'accent--text': showEditMode }" data-testid="edit-button"
                @click="showEditMode = true, showBlocks = false">
                <v-icon class="mx-auto">
                  mdi-auto-fix
                </v-icon>
              </v-btn>
              <span :style="{ cursor: 'pointer' }" :class="{ 'accent--text': showEditMode }"
                @click="showEditMode = true, showBlocks = false">{{ $t('editorEditModeTitle') }}</span>
            </div>
          </v-col>
          <v-col cols="6" align="center">
            <div class="d-flex flex-column" style="align-items:center">
              <v-btn icon class="mr-0 text-none" :class="{ 'accent--text': showBlocks }" data-testid="blocks-button"
                @click="showBlocks = true, showEditMode = false">
                <v-icon class="mx-auto">
                  mdi-view-grid-plus-outline
                </v-icon>
              </v-btn>
              <span :style="{ cursor: 'pointer' }" :class="{ 'accent--text': showBlocks }"
                @click="showBlocks = true, showEditMode = false">{{ $t('editorBlocksModeTitle') }}</span>
            </div>
          </v-col>
        </v-row>
        <v-divider />
      </template>
      <v-card v-if="showBlocks && !loadingData" height="100%" elevation="0" class="px-2" data-testid="blocks-card">
        <div>
          <draggable class="dragArea pb-5" :list="listChecklistItemTypes"
            :group="{ name: 'blocks', pull: 'clone', put: listChecklistItemTypes }" :clone="dragDropBlockToPageLayout"
            :sort="false" chosen-class="chosen-block" @start="onStart" @end="onEnd">
            <v-card v-for="element in listChecklistItemTypes" :key="element.id" class="page-block"
              data-testid="block-card" :ripple="false" @click="addBlockToPageLayout(element.id)" elevation="4">
              <v-card-subtitle data-testid="block-title-card">
                {{ $t(element.name) }}
              </v-card-subtitle>
              <v-img data-testid="block-img-card" :src="getImgUrl(element.image)" />
              <v-card-actions>
                <v-btn data-testid="block-button-card" text
                  class="mx-auto text-none text-body-2 greytext--text rounded-xxl">
                  {{ $t('addBlockToPageLink') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </draggable>
        </div>
      </v-card>
      <editor-settings v-if="showEditMode && !loadingData" :editor="editor" :editor-extension="editorExtension"
        :editor-extension-properties="editorExtensionProperties" :editor-text-size="editorTextSize"
        :editor-activate-link-option="editorActivateLinkOption" :editor-link-properties="editorLinkProperties"
        :colors="contentColors" page="checklist" @disableChangingEditor="disableChangingEditor"
        @proceedUploading="proceedUploadingInEditor" @changeSelectedTextSize="changeSelectedTextSize"
        @changeRecentColors="changeRecentColors" />
    </v-navigation-drawer>
    <ConfirmationDialog v-if="dialogDraftVisible" ref="confirmationDialogExit" :visible="dialogDraftVisible"
      :cd-title="confirmationDialogTitle" :cd-text="confirmationDialogText" :cd-submit-btn="confirmationDialogSubmitBtn"
      :cd-cancel-btn="confirmationDialogCancelBtn" cd-submit-btn-color="accent" data-testid="confirmation-dialog"
      @close="dialogDraftVisible = false" @proceedSubmit="discardDraft" />
    <ConfirmationDialog v-if="dialogPublishVisible" ref="confirmationDialogPublish" :visible="dialogPublishVisible"
      :cd-title="confirmationDialogTitle" :cd-text="confirmationDialogText" :cd-submit-btn="confirmationDialogSubmitBtn"
      :cd-cancel-btn="confirmationDialogCancelBtn" cd-submit-btn-color="accent" data-testid="confirmation-dialog-publish"
      @input="clickOutside" @close="saveChecklistAsDraft" @proceedSubmit="saveChecklist" />
  </div>
</template>
<script>
import i18n from '../../plugins/i18n'
import AlertMessages from '../../components/AlertMessages'
import { repeatedMethodsMixin } from '../../mixins/repeatedMethodsMixin'
import { getActivity, uploadChecklistCoverImage, getActivityChecklist, changeImageInChecklist, updateChecklistItemsInActivityChecklist, getChecklistItemsAll, discardChecklistDraft, updateActivity } from '../../api/activityApi'
import EditorChecklist from '../../components/EditorChecklist';
import EditorSettings from '../../components/EditorSettings';
import draggable from "vuedraggable";
import { ruleRequired, ruleValidMin2Characters } from '../../rules/formRules.js';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import { templateOfTextBlock } from '../../components/editor_components/checklist/Text.js'
import { templateOfTextImageBlock } from '../../components/editor_components/checklist/TextPlusImage.js'
import { templateOfTextVideoBlock } from '../../components/editor_components/checklist/TextPlusVideo.js'
import { eventBus } from '../../eventbus'
import { uuid } from 'vue-uuid'

export default {
  components: {
    AlertMessages,
    EditorChecklist,
    EditorSettings,
    draggable,
    ConfirmationDialog
  },
  mixins: [repeatedMethodsMixin],
  data() {
    return {
      checklistId: this.$route.params.checklistId,
      activityId: this.$route.params.id,
      occupyInterval: null,
      alertMsg: '',
      alertType: '',
      alertValue: false,
      imageData: null,
      loadingImage: false,
      spaceId: window.$cookies.get('space_id'),
      coverURL: null,
      coverPath: null,
      loadingData: false,
      showBlocks: false,
      showEditMode: true,
      loadingAutosave: false,
      loadingAutosaveFinish: false,
      draftVersion: false,
      description: '<h3>Title</h3><p class="greytext--text">Enter an optional description here to explain the use of this checklist...</p>',
      editorExtension: '',
      editorExtensionProperties: {},
      editorTextSize: 'p-normal',
      editorActivateLinkOption: false,
      editor: null,
      editorLinkProperties: {},
      updateEditorDescription: null,
      updateEditorChecklist: null,
      listChecklistItemTypes: [
        { id: 1, name: 'checklistBlock1', image: 'checklisttext.jpg', html: templateOfTextBlock },
        { id: 2, name: 'checklistBlock2', image: 'checklisttextandimage.jpg', html: templateOfTextImageBlock },
        { id: 3, name: 'checklistBlock3', image: 'checklisttextandvideo.jpg', html: templateOfTextVideoBlock }
      ],
      checklist: [],
      disableChangingEditorVal: false,
      titleRules: ruleRequired('labelTitle').concat(ruleValidMin2Characters('labelTitle')),
      dialogDraftVisible: false,
      dialogPublishVisible: false,
      published: false,
      preventRedirect: false,
      role: '',
      contentColors: [],
      valid: true
    }
  },
  computed: {
    productPlan() {
      return this.$store.state.productPlan;
    },
  },
  watch: {
    showBlocks(val) {
      if (val) {
        this.disableEditors()
      }
    },
    showEditMode(val) {
      if (val) {
        this.enableEditors()
      }
    },
    preventRedirect(val) {
      eventBus.$emit('showDialogOnExit', val)
    }
  },
  mounted() {
    if (this.productPlan.attributes && this.productPlan.attributes.name != 'Free') {
      this.occupyEditor();
      this.occupyInterval = setInterval(() => {
        this.occupyEditor();
      }, 60000)
    }
  },
  async created() {
    eventBus.$on('saveChecklistAsDraft', () => {
      this.saveChecklistAsDraft()
    })

    eventBus.$on('allowRedirect', () => {
      this.preventRedirect = false
    })
    //if someone else is editing
    if (this.$store.state.list.find(user => user.entity_id == this.checklistId)) {
      this.$router.push({ name: 'ViewActivityChecklist', params: { id: this.activityId, checklistId: this.checklistId, preventEditing: true } })
    }

    this.loadingData = true
    await this.getActivity()
    if (this.role == 'participant' || this.role == 'guest') {
      this.$router.push({ name: 'PageAccessDenied' })
    }
    await this.getActivityChecklist()

    //create element just for manipulating checkitems images
    if (this.checklist.length > 0) {
      await this.changeTemporaryURLs()
    }

    this.loadingData = false

    //start autosaving if some change is done on the page (image, description or checklist)
    this.$watch('checklist', function () {
      if (!this.preventRedirect) {
        this.preventRedirect = true
        //autosave draft version every 15s
        this.autosaveInterval = setInterval(() => this.autosaveChecklistAsDraft(true), 15000);
      }
    }, { deep: true })

    this.$watch('coverPath', function () {
      if (!this.preventRedirect) {
        this.preventRedirect = true
        //autosave draft version every 15s
        this.autosaveInterval = setInterval(() => this.autosaveChecklistAsDraft(true), 15000);
      }
    })

    this.$watch('description', function () {
      if (!this.preventRedirect) {
        this.preventRedirect = true
        //autosave draft version every 15s
        this.autosaveInterval = setInterval(() => this.autosaveChecklistAsDraft(true), 15000);
      }
    })
  },
  beforeMount() {
    window.addEventListener("beforeunload", this.preventNav)
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.preventNav);
    eventBus.$off('saveChecklistAsDraft')
    eventBus.$off('allowRedirect')
    clearInterval(this.autosaveInterval)
    clearInterval(this.occupyInterval)
  },
  methods: {
    occupyEditor() {
      var payload = { 'key1': this.checklistId, 'key2': this.activityId, 'key3': 'checklist' }
      this.$store.dispatch("occupyEditor", payload).catch(() => {
        this.alertValue = true
        this.alertType = 'error'
        this.alertMsg = i18n.t('serverError')
      })
    },
    releaseEditor() {
      var payload = { 'key1': this.checklistId, 'key2': this.activityId, 'key3': 'checklist' }
      this.$store.dispatch("releaseEditor", payload)
        .catch(() => {
          this.alertValue = true
          this.alertType = 'error'
          this.alertMsg = i18n.t('serverError')

        })
    },
    async getActivity() {
      try {
        const response = await getActivity(this.activityId)
        if (response.status == 200) {
          this.role = response.data.data.attributes.role
          this.contentColors = response.data.data.attributes.content_colors == null ? [] : response.data.data.attributes.content_colors
        }
      } catch (e) {
        if (e.response) {
          if (e.response.status == 404) {
            if (this.isAuth) {
              this.$router.push({ name: 'PageNotFound' })
            } else {
              this.$router.push({ name: 'CustomLogin', query: { referer: this.$route.fullPath } })
            }
          } else if (e.response.status == 403) {
            this.$router.push({ name: 'PageAccessDenied' })
          } else {
            this.alertValue = true
            this.alertType = 'error'
            this.alertMsg = i18n.t('serverError')
          }
        }
      }
    },
    async getActivityChecklist() {
      try {
        const response = await getActivityChecklist(this.checklistId)
        if (response.status == 200) {
          const response_checkitems = await getChecklistItemsAll(this.checklistId)
          if (response_checkitems.status == 200) {
            this.draftVersion = response.data.data.attributes.has_draft
            this.published = response.data.data.attributes.published
            if (this.draftVersion) {
              if (response.data.data.attributes.description != null) {
                this.description = response.data.data.attributes.description
              } else {
                this.description = '<h3>Title</h3><p class="greytext--text">Enter an optional description here to explain the use of this checklist...</p>'
              }
              if (response.data.data.attributes.image != null) {
                this.coverURL = response.data.data.attributes.image_url
                this.coverPath = response.data.data.attributes.image
              } else {
                this.coverURL = null
                this.coverPath = null
              }
            } else {
              if (response.data.data.attributes.published_description != null) {
                this.description = response.data.data.attributes.published_description
              } else {
                this.description = '<h3>Title</h3><p class="greytext--text">Enter an optional description here to explain the use of this checklist...</p>'
              }
              this.coverURL = response.data.data.attributes.published_image_url
              this.coverPath = response.data.data.attributes.published_image
            }
            response_checkitems.data.data.filter(item => item.attributes.is_deleted == 0).forEach(checklistItem => {
              if (this.draftVersion) {
                this.checklist.push({ title: checklistItem.attributes.title, content: checklistItem.attributes.content, id: this.checklist.length, id_temp: checklistItem.id, cid: checklistItem.attributes.cid });
              } else {
                this.checklist.push({ title: checklistItem.attributes.published_title, content: checklistItem.attributes.published_content, id: this.checklist.length, id_temp: checklistItem.id, cid: checklistItem.attributes.cid });
              }
            })
          }
        }
      } catch (e) {
        if (e.response) {
          if (e.response.status == 404) {
            this.$router.push({ name: 'PageNotFound' })
          } else {
            this.alertValue = true
            this.alertType = 'error';
            this.alertMsg = i18n.t('serverError');
          }
        }
      }

    },
    async changeTemporaryURLs() {
      for (var index = 0; index < this.checklist.length; index++) {
        var element = document.createElement('div');
        element.insertAdjacentHTML('beforeend', this.checklist[index].content);
        if (element.querySelectorAll("[path]").length > 0) {
          const key = element.querySelector("[path]").attributes.path.nodeValue
          let data = {
            data: {
              type: "checklists",
              id: this.checklistId,
              attributes: {
                paths: [element.querySelector("[path]").attributes.path.nodeValue],
                space_id: this.spaceId
              }
            }
          };
          const response = await changeImageInChecklist(this.checklistId, data)
          //find and change in html
          element.querySelector('[path="' + key + '"]').attributes.src.nodeValue = response.data.meta.files[key]
          this.checklist[index].content = (element.innerHTML).replace(/&amp;/g, '&');
          this.updateEditorChecklist = Date.now()
        }
      }
    },
    showUploadChecklistImage() {
      this.editorExtension = ''
      this.$refs.file_input.$refs.input.click()
    },
    async uploadImage() {
      if (this.imageData) {
        if (this.imageData.size > 5 * 1024 * 1024) {
          this.alertMsg = i18n.t('uploadCoverError')
          this.alertValue = true
          this.alertType = 'error'
        } else {
          this.loadingImage = true
          try {
            let data = new FormData();
            data.append('file', this.imageData);
            data.append('type', 'checklist');
            data.append('space_id', this.spaceId);

            const response = await uploadChecklistCoverImage(data, this.checklistId)
            if (response.status == 200) {
              this.loadingImage = false
              this.coverURL = URL.createObjectURL(this.imageData)
              this.coverPath = response.data.meta.detail
            }
          } catch (e) {
            if (e.response) {
              if (e.response.status == 422) {
                for (let i = 0; i < e.response.data.errors.length; i++) {
                  this.alertValue = true
                  this.alertType = 'error'
                  if (e.response.data.errors[i].meta[0].code == 'extensionUnsupported' || e.response.data.errors[i].meta[0].code == 'fileOversize') {
                    this.alertMsg = i18n.t('uploadCoverError')
                  } else {
                    this.alertMsg = i18n.t('serverError')
                  }
                }
              } else if (e.response.status == 403) {
                for (let i = 0; i < e.response.data.errors.length; i++) {
                  if (e.response.data.errors[i].detail == 'Storage is full') {
                    this.alertValue = true
                    this.alertType = 'error'
                    this.alertMsg = i18n.t('storageFullError')
                  }
                  else {
                    this.$router.push({ name: 'PageAccessDenied' })
                  }
                }
              } else {
                this.alertType = 'error'
                this.alertValue = true
                this.alertMsg = i18n.t('serverError')
              }
            } else {
              this.alertType = 'error'
              this.alertValue = true
              this.alertMsg = i18n.t('serverError')
            }
            this.loadingImage = false
          }
        }
      }
      this.$refs.file_input.$refs.input.value = ''
    },
    async removeImage() {
      this.loadingImage = true
      this.coverPath = null
      this.coverURL = null
      this.imageData = null
      this.loadingImage = false
    },
    setEditor(editorSelected) {
      if (editorSelected == null) {
        this.editor = null
      } else {
        if (!this.disableChangingEditorVal) {
          this.editor = editorSelected
          this.$nextTick(async () => {
            if (editorSelected.view.state.selection.node) {
              this.editorExtension = editorSelected.view.state.selection.node.type.name
              this.editorExtensionProperties = editorSelected.view.state.selection.node
              if (this.editorExtensionProperties.attrs.path != null) {
                let data = {
                  data: {
                    type: "checklists",
                    id: this.checklistId,
                    attributes: {
                      paths: [this.editorExtensionProperties.attrs.path],
                      space_id: this.spaceId
                    }
                  }
                };
                const response = await changeImageInChecklist(this.checklistId, data)
                this.editorExtensionProperties.attrs.src = response.data.meta.files[this.editorExtensionProperties.attrs.path]
              }
            } else {
              if (this.editor.isActive('details')) {
                if (this.editor.isActive('detailsSummary')) {
                  this.editorExtension = 'accordion-summary'
                } else {
                  this.editorExtension = 'accordion-content'
                }
              }
              else if (this.editor.contentComponent.$el.classList.contains('document')) {
                this.editorExtension = 'document'
                this.editor.setOptions({ editable: false });

              }
              else {
                this.editorExtension = 'text'
                if (this.editor.isActive('custom-link')) {
                  this.editorLinkProperties = this.editor.getAttributes('custom-link')
                } else if (this.editor.isActive('heading')) {
                  this.editorTextSize = 'h' + this.editor.getAttributes('heading').level
                } else {
                  if (this.editor.getAttributes('paragraph').class.includes('small')) {
                    this.editorTextSize = 'p-small'
                  } else {
                    this.editorTextSize = 'p-normal'
                  }

                }
              }
            }
          })
        }

      }
    },
    changeSelectedTextSize(val) {
      this.editorTextSize = val
    },
    async changeRecentColors(val) {
      let data = {
        data: {
          type: "activities",
          id: this.activityId,
          attributes: {
            content_colors: val
          },
          relationships: {
            space: {
              data: {
                type: "spaces",
                id: this.spaceId
              }
            },
          }
        }
      }
      const response = await updateActivity(this.activityId, data)
      if (response.status == 200) {
        this.contentColors = val
      }
    },
    updateContent(content) {
      if (content != '') {
        this.description = content
      }
    },
    updateChecklist(index, content) {
      if (content != '') {
        this.checklist[index]['content'] = content
      }
    },
    showHideEditorTextOptions(val) {
      this.editorActivateLinkOption = val
    },
    disableEditors() {
      if (this.$refs.editChecklist && Array.isArray(this.$refs.editChecklist)) {
        this.$refs.editChecklist.forEach((value, index) => {
          if (this.$refs.editChecklist[index].editorChecklist.isEditable) {
            this.$refs.editChecklist[index].editorChecklist.setEditable(false)
          }
        });
      }
      if (this.$refs.editDescription.editorDescription.isEditable) {
        this.$refs.editDescription.editorDescription.setEditable(false)
      }
    },
    enableEditors() {
      if (this.$refs.editChecklist && Array.isArray(this.$refs.editChecklist)) {
        this.$refs.editChecklist.forEach((value, index) => {
          if (!this.$refs.editChecklist[index].editorChecklist.isEditable) {
            this.$refs.editChecklist[index].editorChecklist.setEditable(true)
          }
        });
      }
      if (!this.$refs.editDescription.editorDescription.isEditable) {
        this.$refs.editDescription.editorDescription.setEditable(true)
      }
    },
    removeDescription() {
      this.description = '<p class="greytext--text"></p>'
      this.updateEditorDescription = Date.now()
      this.$nextTick(() => {
        if (this.showBlocks) {
          this.$refs.editPage.editorDescription.setEditable(false)
        }
      })
    },
    getImgUrl(element) {
      var images = require.context('../../assets/editor_images', false, /\.jpg$/)
      return images('./' + element)
    },

    dragDropBlockToPageLayout(evt) {
      return {
        title: 'Example title',
        content: evt.html,
        id: this.checklist.length,
        id_temp: null,
        cid: uuid.v4()
      }
    },
    onEnd() {
      // if(this.showBlocks){
      //     this.disableEditors()
      // }
      this.updateEditorChecklist = Date.now()
    },
    onStart() {
      this.$refs.dragArea._sortable.options.draggable = ">*"
    },
    addBlockToPageLayout: async function (id) {
      var listItem = this.listChecklistItemTypes.find(item => item.id === id)
      await this.checklist.push({ title: 'Example title', content: listItem.html, id: this.checklist.length, id_temp: null, cid: uuid.v4() });
      this.$nextTick(() => {
        window.scrollTo(0, document.body.scrollHeight || document.documentElement.scrollHeight);
      })
      // if(this.showBlocks){
      //     this.disableEditors()
      // }
      this.updateEditorChecklist = Date.now()
    },
    log: function () {
      //window.console.log(evt)
    },
    moveBlock(from, to) {
      this.checklist.splice(to, 0, this.checklist.splice(from, 1)[0])
      // if(this.showBlocks){
      //     this.disableEditors()
      // }
      this.updateEditorChecklist = Date.now()
    },
    deleteBlock(element) {
      this.checklist.splice(element, 1);
      // if(this.showBlocks){
      //     this.disableEditors()
      // }
      this.updateEditorChecklist = Date.now()
      this.editorExtension = ''
      this.editorExtensionProperties = {}
    },
    copyBlock(e, element, index) {
      this.checklist.splice(index + 1, 0, { title: this.checklist[index].title, content: this.checklist[index].content, id: this.checklist.length + 1, id_temp: null, cid: uuid.v4() });
      // if(this.showBlocks){
      //     this.disableEditors()
      // }
      this.updateEditorChecklist = Date.now()
    },
    proceedUploadingInEditor(alertMsg) {
      this.alertValue = true
      this.alertType = 'error';
      this.alertMsg = alertMsg
    },
    disableChangingEditor(val) {
      this.disableChangingEditorVal = val
    },

    async saveChecklistAsDraft() {
      var checklistItems = []
      this.checklist.forEach(checklistItem => {
        checklistItems.push({ title: checklistItem.title, content: checklistItem.content.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, ''), id: checklistItem.id_temp, cid: checklistItem.cid });
      })
      try {
        let data = {
          data: {
            type: "checklists",
            id: this.checklistId,
            attributes: {
              published: false,
              image: this.coverPath,
              description: this.description,
              checkitems: checklistItems
            },
            relationships: {
              activity: {
                data: {
                  type: "activities",
                  id: this.activityId
                }
              }
            }
          }
        };
        const response = await updateChecklistItemsInActivityChecklist(this.checklistId, data)
        if (response.status == 200) {
          if (this.productPlan.attributes && this.productPlan.attributes.name != 'Free') {
            this.releaseEditor();
          }
          this.$router.push({ name: 'ViewActivityChecklist', params: { id: this.$route.params.id, checklistId: this.checklistId, successfullyUpdatedActivityChecklistDraft: true } })
        }
      } catch (e) {
        if (e.response) {
          if (e.response.status == 403) {
            for (let i = 0; i < e.response.data.errors.length; i++) {
              if (e.response.data.errors[i].detail == 'This action is unauthorized.') {
                this.$router.push({ name: 'PageAccessDenied' })
              }
              else {
                this.alertValue = true
                this.alertType = 'error'
                this.alertMsg = i18n.t('serverError')
              }
            }
          } else {
            this.alertValue = true
            this.alertType = 'error'
            this.alertMsg = i18n.t('serverError')
          }
        }
      }
    },
    async autosaveChecklistAsDraft(autosave) {
      var checklistItems = []
      this.loadingAutosave = true
      this.loadingAutosaveFinish = false
      this.checklist.forEach(checklistItem => {
        checklistItems.push({ title: checklistItem.title, content: checklistItem.content.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, ''), id: checklistItem.id_temp, cid: checklistItem.cid });
      })
      try {
        let data = {
          data: {
            type: "checklists",
            id: this.checklistId,
            attributes: {
              published: false,
              image: this.coverPath,
              description: this.description,
              checkitems: checklistItems
            },
            relationships: {
              activity: {
                data: {
                  type: "activities",
                  id: this.activityId
                }
              }
            }
          }
        };
        const response = await updateChecklistItemsInActivityChecklist(this.checklistId, data)
        if (response.status == 200) {
          this.draftVersion = true
          //get all checkitems again, to populate id for newly added checkitems
          const response_checkitems = await getChecklistItemsAll(this.checklistId)
          if (response_checkitems.status == 200) {
            response_checkitems.data.data.filter(item => item.attributes.is_deleted == 0).forEach(checklistItem => {
              let checkitem = this.checklist.find(item => item.cid == checklistItem.attributes.cid)
              if (checkitem.id_temp == null) {
                checkitem.id_temp = checklistItem.id
              }
            })
          }
        }
      } catch (e) {
        if (e.response) {
          if (e.response.status == 403) {
            for (let i = 0; i < e.response.data.errors.length; i++) {
              if (e.response.data.errors[i].detail == 'This action is unauthorized.') {
                this.$router.push({ name: 'PageAccessDenied' })
              }
              else {
                this.alertValue = true
                this.alertType = 'error'
                this.alertMsg = i18n.t('serverError')
              }
            }
          } else {
            this.alertValue = true
            this.alertType = 'error'
            this.alertMsg = i18n.t('serverError')
          }
        }
      }
      if (autosave) {
        this.loadingAutosave = false
      } else {
        this.loadingAutosave = false
        this.loadingAutosaveFinish = true
        setTimeout(() => { this.loadingAutosaveFinish = false }, 2000);
      }
    },

    async saveChecklist() {
      this.valid = this.$refs.form ? this.$refs.form.validate() : true
      if (this.valid) {
        var checklistItems = []
        this.checklist.forEach(checklistItem => {
          checklistItems.push({ title: checklistItem.title, content: checklistItem.content.replace(/add-transparent-border /g, '').replace(/add-transparent-border/g, ''), id: checklistItem.id_temp, cid: checklistItem.cid });
        })
        this.preventRedirect = false

        //now send to server
        try {
          let data = {
            data: {
              type: "checklists",
              id: this.checklistId,
              attributes: {
                published: true,
                image: this.coverPath,
                description: this.description,
                checkitems: checklistItems
              },
              relationships: {
                activity: {
                  data: {
                    type: "activities",
                    id: this.activityId
                  }
                }
              }
            }
          };
          const response = await updateChecklistItemsInActivityChecklist(this.checklistId, data)
          if (response.status == 200) {
            if (this.productPlan.attributes && this.productPlan.attributes.name != 'Free') {
              this.releaseEditor();
            }
            this.$router.push({ name: 'ViewActivityChecklist', params: { id: this.$route.params.id, checklistId: this.checklistId, successfullyUpdatedActivityChecklist: true } })
          }
        } catch (e) {
          if (e.response) {
            if (e.response.status == 403) {
              for (let i = 0; i < e.response.data.errors.length; i++) {
                if (e.response.data.errors[i].detail == 'This action is unauthorized.') {
                  this.$router.push({ name: 'PageAccessDenied' })
                }
                else {
                  this.alertValue = true
                  this.alertType = 'error'
                  this.alertMsg = i18n.t('serverError')
                }
              }
            } else if (e.response.status == 404) {
              this.$router.push({ name: 'PageNotFound' })
            } else {
              this.alertValue = true
              this.alertType = 'error'
              this.alertMsg = i18n.t('serverError')
            }
            this.dialogPublishVisible = false
          }
        }
      } else {
        this.alertValue = true
        this.alertType = 'error'
        this.alertMsg = i18n.t('checkitemValidationError')
        this.dialogPublishVisible = false
      }
    },
    async discardDraft() {
      this.loadingData = true
      try {
        const response = await discardChecklistDraft(this.checklistId)
        if (response.status) {
          if (response.status == 200) {
            this.dialogDraftVisible = false
            this.checklist = []
            this.draftVersion = false
            await this.getActivityChecklist()
          }
        }
      } catch (e) {
        if (e.response) {
          if (e.response.status == 403) {
            this.$router.push({ name: 'PageAccessDenied' })
          } else {
            this.dialogDraftVisible = false
            this.alertValue = true
            this.alertType = 'error'
            this.alertMsg = i18n.t('serverError')
          }
        }
      }

      this.editorExtension = ''
      this.editorExtensionProperties = {}

      this.loadingData = false
    },
    disableDesigning() {
      if (this.showEditMode) {
        this.setEditor(null)
      }
    },
    clickOutside() {
      this.dialogPublishVisible = false
    },
    preventNav(event) {
      if (this.preventRedirect) {
        event.preventDefault()
        event.returnValue = ""
      }
    },
    beforeRouteLeave(to, from, next) {
      if (this.preventRedirect) {
        const answer = window.confirm('Do you really want to leave? Changes you made may not be saved.')
        if (answer) {
          if (this.productPlan.attributes && this.productPlan.attributes.name != 'Free') {
            this.releaseEditor();
          }
          next()
        } else {
          next(false)
        }
      } else {
        next()
      }
    },
  }
}

</script>